import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProjectModal from './ProjectModal';
import { fetchProjects, deleteProject } from '../services/api';
import ThemeToggle from './ThemeToggle';
import './MainPage.css';
import labBackground from '../assets/images/lab2-background.jpg';

// Enhanced styles for better hover effects and transitions
const enhancedStyles = `
  .project-list-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0,0,0,0.1) !important;
    border-color: #1e40af !important;
  }
  
  .stats-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
  }
  
  .quick-action-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  }
  
  .nav-link:hover {
    border-bottom-color: rgba(96, 165, 250, 0.8) !important;
    color: #93C5FD !important;
  }
  
  @media (max-width: 768px) {
    .nav {
      display: none;
    }
    .col-md-3, .col-md-6 {
      flex: 0 0 auto;
      width: 50%;
    }
  }
`;

const infoLinks = [
  {
    label: 'Official Blog',
    description: 'Stay updated with latest news and announcements',
    url: 'https://devblogs.microsoft.com/visualstudio/',
    icon: '📰'
  },
  {
    label: 'Documentation',
    description: 'Learn more about features and best practices',
    url: 'https://code.visualstudio.com/docs',
    icon: '📚'
  },
  {
    label: 'GitHub Repository',
    description: 'Access source code and contribute to projects',
    url: 'https://github.com/',
    icon: '💻'
  },
  {
    label: 'Community Forum',
    description: 'Connect with other developers and share knowledge',
    url: 'https://stackoverflow.com/',
    icon: '👥'
  }
];

function MainPage({ onLogout }) {
  const navigate = useNavigate();
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [createdProject, setCreatedProject] = useState(null);
  const [currentUser] = useState('Admin User');
  const [showChartModal, setShowChartModal] = useState(false);
  const [selectedChart, setSelectedChart] = useState(null);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
    navigate('/login');
  };

  useEffect(() => {
    loadProjects();
    
    // Load Datta Able theme assets
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/assets/css/style.css';
    document.head.appendChild(link);

    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = '/assets/fonts/tabler-icons.min.css';
    document.head.appendChild(fontLink);

    // Inject enhanced styles
    const styleElement = document.createElement('style');
    styleElement.textContent = enhancedStyles;
    document.head.appendChild(styleElement);

    // Note: Theme attributes are now managed by ThemeContext

    return () => {
      if (document.head.contains(link)) document.head.removeChild(link);
      if (document.head.contains(fontLink)) document.head.removeChild(fontLink);
      if (document.head.contains(styleElement)) document.head.removeChild(styleElement);
    };
  }, []);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      setError('');
      console.log('Frontend: Fetching projects from API...');
      const response = await fetchProjects();
      console.log('Frontend: Received response:', response);
      console.log(`Frontend: Found ${response.projects?.length || 0} projects`);
      
      if (response.projects && response.projects.length > 0) {
        console.log('Frontend: Project details:');
        response.projects.forEach((project, index) => {
          console.log(`Project ${index + 1}:`, {
            id: project.projectId,
            name: project.projectName,
            owner: project.projectOwner,
            scale: project.scale,
            type: project.type,
            runs: project.numberOfRuns,
            created: project.createdAt
          });
        });
      } else {
        console.log('Frontend: No projects found');
      }
      
      setProjects(response.projects || []);
    } catch (error) {
      console.error('Frontend Error loading projects:', error);
      setError('Failed to load projects. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProjectCreated = async (newProject) => {
    setShowProjectModal(false);
    
    if (newProject && newProject.projectId) {
      setProjects(prevProjects => [...prevProjects, newProject]);
      setCreatedProject(newProject);
      setShowConfirmationModal(true);
    }
    
    setTimeout(async () => {
      try {
        await loadProjects();
      } catch (error) {
        console.error('Error reloading projects after creation:', error);
      }
    }, 100);
  };

  const handleGoToProjectLanding = () => {
    setShowConfirmationModal(false);
    if (createdProject && createdProject.projectId) {
      navigate(`/project/${createdProject.projectId}`);
    }
  };

  const handleStayOnMainPage = () => {
    setShowConfirmationModal(false);
    setCreatedProject(null);
  };

  const handleDeleteClick = (e, project) => {
    e.preventDefault();
    e.stopPropagation();
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;
    
    try {
      setIsDeleting(true);
      setError('');
      
      console.log('Deleting project:', projectToDelete.projectId);
      await deleteProject(projectToDelete.projectId);
      
      const storedProjects = JSON.parse(localStorage.getItem('projects') || '[]');
      const updatedProjects = storedProjects.filter(p => p.projectId !== projectToDelete.projectId);
      localStorage.setItem('projects', JSON.stringify(updatedProjects));
      
      await loadProjects();
      
      setShowDeleteModal(false);
      setProjectToDelete(null);
    } catch (error) {
      console.error('Error deleting project:', error);
      setError(`Failed to delete project: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setProjectToDelete(null);
  };

  const handleShowChart = (chartType) => {
    setSelectedChart(chartType);
    setShowChartModal(true);
  };

  const handleCloseChart = () => {
    setShowChartModal(false);
    setSelectedChart(null);
  };

  const renderChart = () => {
    if (!selectedChart) return null;

    const chartData = {
      'project-trend': {
        title: 'Project Trends Over Time',
        data: projects.map((_, index) => ({
          month: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'][index % 6],
          value: Math.floor(Math.random() * 50) + 20
        }))
      },
      'success-rate': {
        title: 'Success Rate Analysis',
        data: [
          { category: 'Completed', value: 92, color: '#059669' },
          { category: 'In Progress', value: 75, color: '#1e40af' },
          { category: 'Planning', value: 60, color: '#d97706' }
        ]
      },
      'resource-usage': {
        title: 'Resource Utilization Timeline',
        data: Array.from({length: 12}, (_, i) => ({
          hour: `${i + 1}:00`,
          usage: Math.floor(Math.random() * 40) + 40
        }))
      }
    };

    const currentData = chartData[selectedChart];
    if (!currentData) return null;

    return (
      <div>
        <h6 className="fw-bold mb-4">{currentData.title}</h6>
        {selectedChart === 'success-rate' ? (
          // Bar Chart for Success Rate
          <div className="chart-container">
            {currentData.data.map((item, index) => (
              <div key={index} className="mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <span className="small fw-medium">{item.category}</span>
                  <span className="small fw-bold" style={{ color: item.color }}>{item.value}%</span>
                </div>
                <div className="progress" style={{ height: '12px', borderRadius: '6px' }}>
                  <div 
                    className="progress-bar" 
                    style={{ 
                      width: `${item.value}%`, 
                      backgroundColor: item.color,
                      borderRadius: '6px'
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Line Chart representation for other charts
          <div className="chart-container">
            <div className="row">
              {currentData.data.map((item, index) => (
                <div key={index} className="col-2 text-center mb-3">
                  <div 
                    className="chart-bar mx-auto mb-2" 
                    style={{ 
                      height: `${item.value || item.usage}px`,
                      width: '20px',
                      backgroundColor: '#1e40af',
                      borderRadius: '3px'
                    }}
                  ></div>
                  <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                    {item.month || item.hour}
                  </small>
                </div>
              ))}
            </div>
            <div className="text-center mt-3">
              <small className="text-muted">Interactive chart visualization</small>
            </div>
          </div>
        )}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }  };

  return (
    <div className="main-page">
      {/* Pre-loader */}
      <div className="loader-bg" style={{ display: isLoading ? 'flex' : 'none' }}>
        <div className="loader-track">
          <div className="loader-fill"></div>        </div>
      </div>

      {/* Header - Enhanced Design matching Homepage.png */}
      <header className="w-100 shadow-sm position-fixed" style={{ 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 1050, 
        height: '60px', 
        background: 'linear-gradient(135deg, #172554 0%, #1E3A8A 50%, #3B82F6 100%)'
      }}>
        <div className="container-fluid px-4 h-100">
          <div className="row align-items-center h-100">
            {/* Logo Section */}
            <div className="col-md-3">
              <Link to="/" className="d-flex align-items-center text-decoration-none">
                <img src="/assets/images/RapidCMCLogo.jpeg" alt="RapidCMC" style={{ height: '28px' }} />
                <span className="ms-2 fw-bold fs-4" style={{ color: '#ffffff' }}>RapidCMC</span>
              </Link>
            </div>

            {/* Navigation Section */}
            <div className="col-md-6">
              <nav className="d-flex justify-content-center">
                <ul className="nav" style={{ gap: '1.5rem' }}>
                  <li className="nav-item">
                    <a className="nav-link px-0" href="#" style={{ 
                      fontWeight: '500',
                      fontSize: '0.9rem',
                      borderBottom: '2px solid transparent',
                      paddingBottom: '0.4rem',
                      transition: 'all 0.3s ease',
                      color: '#60A5FA !important'
                    }}>
                      <i className="ti ti-layout-dashboard me-2"></i>Dashboard
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link px-0" href="#" style={{ 
                      fontWeight: '500',
                      fontSize: '0.9rem',
                      borderBottom: '2px solid transparent',
                      paddingBottom: '0.4rem',
                      transition: 'all 0.3s ease',
                      color: '#60A5FA !important'
                    }}>
                      <i className="ti ti-folder me-2"></i>Projects
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link px-0" href="#" style={{ 
                      fontWeight: '500',
                      fontSize: '0.9rem',
                      borderBottom: '2px solid transparent',
                      paddingBottom: '0.4rem',
                      transition: 'all 0.3s ease',
                      color: '#60A5FA !important'
                    }}>
                      <i className="ti ti-database me-2"></i>Data
                    </a>
                  </li>
                </ul>
              </nav>
            </div>

            {/* User Section */}
            <div className="col-md-3">
              <div className="d-flex justify-content-end align-items-center">
                {/* Notification Bell */}
                <button className="btn btn-link me-3" style={{ 
                  fontSize: '1.1rem',
                  color: '#ffffff !important'
                }}>
                  <i className="ti ti-bell"></i>
                </button>
                
                <ThemeToggle className="mx-1" />
                
                <div className="dropdown ms-2">
                  <button
                    className="btn d-flex align-items-center admin-user-btn"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    style={{ 
                      backgroundColor: 'rgba(96, 165, 250, 0.2)', 
                      border: '1px solid rgba(96, 165, 250, 0.4)',
                      borderRadius: '25px',
                      color: 'white',
                      position: 'relative',
                      paddingRight: '30px'
                    }}
                  >
                    <i className="ti ti-user-circle me-2" style={{ 
                      color: 'white'
                    }}></i>
                    <span className="d-none d-lg-inline admin-username" style={{ 
                      color: 'white'
                    }}>{currentUser}</span>
                    <span style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'white',
                      fontSize: '0.8rem'
                    }}>▼</span>
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end">
                    <li className="dropdown-header bg-primary text-white">
                      <div className="d-flex align-items-center">
                        <i className="ti ti-user-circle me-2" style={{ fontSize: '1.5rem' }}></i>
                        <div>
                          <h6 className="mb-0">{currentUser}</h6>
                          <small>Administrator</small>
                        </div>
                      </div>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    <li><a className="dropdown-item" href="#"><i className="ti ti-settings me-2"></i>Settings</a></li>
                    <li><a className="dropdown-item" href="#"><i className="ti ti-user me-2"></i>Profile</a></li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <button className="dropdown-item text-danger" onClick={handleLogout}>
                        <i className="ti ti-logout me-2"></i>Logout
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Full Width */}
      <div className="w-100 main-content" style={{ marginTop: '60px', minHeight: 'calc(100vh - 60px)', backgroundColor: '#f8fafc' }}>
        <div className="container-fluid px-4 py-4">
          {/* Main Content */}
          <div className="row">
            {/* Enhanced Welcome Section - Matching Homepage.png */}
            <div className="col-md-12 col-xl-12 mb-4">
              <div className="card border-0 shadow-sm" style={{ 
                borderRadius: '15px'
              }}>
                <div className="card-body p-5" style={{
                  backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5)), url(${labBackground})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  borderRadius: '15px',
                  minHeight: '300px'
                }}>
                  <div className="text-center">
                    <h1 className="h2 fw-bold mb-3" style={{ color: '#1e293b' }}>
                      Welcome to Rapid CMC SaaS
                    </h1>
                    <p className="lead mb-4" style={{ 
                      color: '#64748b', 
                      fontSize: '1.2rem',
                      maxWidth: '800px',
                      margin: '0 auto'
                    }}>
                      Manage rapid CMC projects efficiently by comprehensive platform. Manage projects, manage in new
                    </p>
                    <div className="d-flex justify-content-center">
                      <button
                        className="btn btn-primary px-4 py-2"
                        onClick={() => setShowProjectModal(true)}
                        style={{ 
                          backgroundColor: 'var(--primary) !important', 
                          border: 'none !important',
                          borderRadius: '25px !important',
                          color: '#ffffff !important',
                          fontWeight: '600 !important',
                          fontSize: '1rem !important',
                          boxShadow: '0 4px 15px rgba(30, 58, 138, 0.3) !important',
                          cursor: 'pointer !important',
                          outline: 'none !important',
                          textDecoration: 'none !important',
                          WebkitTextFillColor: '#ffffff !important',
                          MozTextFillColor: '#ffffff !important',
                          textColor: '#ffffff !important',
                          forcedColorAdjust: 'none !important'
                        }}
                      >
                        + Create New Project
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>            {/* Enhanced Statistics Cards - Preserving all data logic */}
            {/* Compact Statistics Cards */}
            <div className="col-12 col-sm-6 col-lg-3 mb-4">
              <div className="card h-100 border-0 shadow-sm stats-card" style={{ 
                borderRadius: '12px',
                minHeight: '120px'
              }}>
                <div className="card-body p-3 d-flex align-items-center h-100">
                  <div className="d-flex align-items-center w-100">
                    <div className="rounded-circle p-2 me-3" style={{ 
                      backgroundColor: '#e0f2fe', 
                      width: '40px', 
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <i className="ti ti-folder" style={{ fontSize: '1.2rem', color: '#0284c7' }}></i>
                    </div>
                    <div className="flex-grow-1">
                      <h4 className="fw-bold mb-1" style={{ color: '#1e293b', fontSize: '1.4rem' }}>
                        {projects.length}
                      </h4>
                      <p className="text-muted mb-0 small">Total Projects</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-lg-3 mb-4">
              <div className="card h-100 border-0 shadow-sm stats-card" style={{ 
                borderRadius: '12px',
                minHeight: '120px'
              }}>
                <div className="card-body p-3 d-flex align-items-center h-100">
                  <div className="d-flex align-items-center w-100">
                    <div className="rounded-circle p-2 me-3" style={{ 
                      backgroundColor: '#dcfce7', 
                      width: '40px', 
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <i className="ti ti-activity" style={{ fontSize: '1.2rem', color: '#16a34a' }}></i>
                    </div>
                    <div className="flex-grow-1">
                      <h4 className="fw-bold mb-1" style={{ color: '#1e293b', fontSize: '1.4rem' }}>
                        {projects.filter(p => p.numberOfRuns > 0).length}
                      </h4>
                      <p className="text-muted mb-0 small">Active Projects</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-lg-3 mb-4">
              <div className="card h-100 border-0 shadow-sm stats-card" style={{ 
                borderRadius: '12px',
                minHeight: '120px'
              }}>
                <div className="card-body p-3 d-flex align-items-center h-100">
                  <div className="d-flex align-items-center w-100">
                    <div className="rounded-circle p-2 me-3" style={{ 
                      backgroundColor: '#fef3c7', 
                      width: '40px', 
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <i className="ti ti-trending-up" style={{ fontSize: '1.2rem', color: '#d97706' }}></i>
                    </div>
                    <div className="flex-grow-1">
                      <h4 className="fw-bold mb-1" style={{ color: '#1e293b', fontSize: '1.4rem' }}>
                        {projects.reduce((total, project) => total + (project.numberOfRuns || 0), 0)}
                      </h4>
                      <p className="text-muted mb-0 small">Total Runs</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-lg-3 mb-4">
              <div className="card h-100 border-0 shadow-sm stats-card" style={{ 
                borderRadius: '12px',
                minHeight: '120px'
              }}>
                <div className="card-body p-3 d-flex align-items-center h-100">
                  <div className="d-flex align-items-center w-100">
                    <div className="rounded-circle p-2 me-3" style={{ 
                      backgroundColor: '#e0e7ff', 
                      width: '40px', 
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <i className="ti ti-users" style={{ fontSize: '1.2rem', color: '#4f46e5' }}></i>
                    </div>
                    <div className="flex-grow-1">
                      <h4 className="fw-bold mb-1" style={{ color: '#1e293b', fontSize: '1.4rem' }}>
                        {new Set(projects.map(p => p.projectOwner)).size}
                      </h4>
                      <p className="text-muted mb-0 small">Team Members</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Recent Projects Section - Preserving all API functionality */}
            <div className="col-md-8 mb-4">
              <div className="card border-0 shadow-lg" style={{ 
                borderRadius: '20px',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(0,0,0,0.05)'
              }}>
                <div className="card-header border-0 p-2" style={{ 
                  borderRadius: '20px 20px 0 0',
                  background: 'linear-gradient(135deg, #172554 0%, #1E3A8A 50%, #3B82F6 100%)',
                  color: 'white'
                }}>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle p-1 me-2" style={{ 
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        backdropFilter: 'blur(10px)',
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <i className="ti ti-folder" style={{ fontSize: '1rem', color: 'white' }}></i>
                      </div>
                      <div>
                        <h6 className="mb-0 fw-bold" style={{ color: 'white', fontSize: '0.95rem' }}>Recent Projects</h6>
                        <p className="mb-0 small opacity-75" style={{ fontSize: '0.7rem' }}>Manage and track your project portfolio</p>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <button 
                        className="btn btn-sm"
                        style={{ 
                          borderRadius: '12px', 
                          fontSize: '0.85rem',
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          border: 'none',
                          color: '#1e40af',
                          fontWeight: '600',
                          padding: '6px 12px'
                        }}
                      >
                        <i className="ti ti-folder me-1"></i>
                        {projects.length} Projects
                      </button>
                      <button 
                        className="btn btn-sm"
                        onClick={loadProjects} 
                        title="Refresh Projects"
                        style={{ 
                          borderRadius: '12px', 
                          fontSize: '0.85rem',
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          border: 'none',
                          color: '#1e40af',
                          fontWeight: '600',
                          cursor: 'pointer',
                          padding: '6px 12px'
                        }}
                      >
                        <i className="ti ti-refresh me-1"></i>
                        Refresh
                      </button>
                    </div>
                  </div>
                </div>
                <div className="card-body p-3">
                  {error && (
                    <div className="alert alert-danger" role="alert" style={{ borderRadius: '12px' }}>
                      <i className="ti ti-alert-circle me-2"></i>
                      {error}
                    </div>
                  )}
                  
                  {isLoading ? (
                    <div className="text-center py-5">
                      <div>
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading projects...</p>
                      </div>
                    </div>
                  ) : (
                    <div className="projects-list-container">
                      {projects.length === 0 ? (
                        <div className="empty-state text-center py-5">
                          <div>
                            <div className="empty-state-icon mb-3">
                              <div className="rounded-circle mx-auto" style={{ 
                                width: '80px', 
                                height: '80px', 
                                backgroundColor: '#f1f5f9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <i className="ti ti-folder" style={{ fontSize: '2rem', color: '#64748b' }}></i>
                              </div>
                            </div>
                            <h6 className="empty-state-title fw-bold mb-2" style={{ color: '#1e293b' }}>No projects yet</h6>
                            <p className="empty-state-description text-muted">Create your first project to get started!</p>
                          </div>
                        </div>
                      ) : (
                        <div className="projects-list">
                          {projects.map((project, index) => {
                            // Generate avatar initials and colors
                            const getInitials = (name) => {
                              return name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'UN';
                            };
                            const avatarColors = ['#1e40af', '#059669', '#d97706', '#dc2626', '#8b5cf6'];
                            const avatarColor = avatarColors[index % avatarColors.length];
                            
                            // Generate status based on project data
                            const getStatus = (project) => {
                              if (project.numberOfRuns > 5) return { text: 'Completed', color: '#059669' };
                              if (project.numberOfRuns > 2) return { text: 'Analysis', color: '#1e40af' };
                              if (project.numberOfRuns > 0) return { text: 'In Progress', color: '#d97706' };
                              return { text: 'Plot Ready', color: '#8b5cf6' };
                            };
                            
                            const status = getStatus(project);
                            
                            return (
                              <div key={project.projectId} className="project-list-item mb-3 p-3" style={{ 
                                border: '1px solid #e2e8f0', 
                                borderRadius: '12px',
                                transition: 'all 0.2s ease',
                                backgroundColor: '#ffffff'
                              }}>
                                <Link to={`/project/${project.projectId}`} className="text-decoration-none">
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center">
                                      {/* Avatar Circle */}
                                      <div 
                                        className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold me-3"
                                        style={{ 
                                          width: '48px', 
                                          height: '48px', 
                                          backgroundColor: avatarColor,
                                          fontSize: '0.9rem'
                                        }}
                                      >
                                        {getInitials(project.projectOwner || project.projectName)}
                                      </div>
                                      
                                      {/* Project Info */}
                                      <div>
                                        <h6 className="mb-1 fw-bold" style={{ color: '#1e293b' }}>
                                          {project.projectName}
                                        </h6>
                                        <div className="d-flex align-items-center text-muted small">
                                          <span className="me-3">
                                            <i className="ti ti-user me-1"></i>
                                            {project.projectOwner || 'Unknown'}
                                          </span>
                                          <span className="me-3">
                                            <i className="ti ti-tag me-1"></i>
                                            {project.type || 'Unknown'}
                                          </span>
                                          <span>
                                            <i className="ti ti-calendar me-1"></i>
                                            {formatDate(project.createdAt)}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <div className="d-flex align-items-center">
                                      {/* Status Badge */}
                                      <span 
                                        className="badge me-3"
                                        style={{ 
                                          backgroundColor: `${status.color}20`,
                                          color: status.color,
                                          borderRadius: '20px',
                                          padding: '6px 12px',
                                          fontSize: '0.75rem',
                                          fontWeight: '600'
                                        }}
                                      >
                                        {status.text}
                                      </span>
                                      
                                      {/* Delete Button */}
                                      <button
                                        className="btn btn-sm"
                                        onClick={(e) => handleDeleteClick(e, project)}
                                        title="Delete Project"
                                        style={{ 
                                          backgroundColor: '#fef2f2',
                                          border: 'none',
                                          borderRadius: '8px',
                                          color: '#dc2626'
                                        }}
                                      >
                                        <i className="ti ti-trash"></i>
                                      </button>
                                    </div>
                                  </div>
                                </Link>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Analytics Overview Section - Enhanced Dashboard Design matching Homepage.png */}
              <div className="card border-0 shadow-lg" style={{ 
                borderRadius: '20px',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(0,0,0,0.05)'
              }}>
                {/* Header with Gradient Background */}
                <div className="card-header border-0 p-2" style={{ 
                  borderRadius: '20px 20px 0 0',
                  background: 'linear-gradient(135deg, #172554 0%, #1E3A8A 50%, #3B82F6 100%)',
                  color: 'white'
                }}>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle p-1 me-2" style={{ 
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        backdropFilter: 'blur(10px)',
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <i className="ti ti-chart-line" style={{ fontSize: '1rem', color: 'white' }}></i>
                      </div>
                      <div>
                        <h6 className="mb-0 fw-bold" style={{ color: 'white', fontSize: '0.95rem' }}>Analytics Dashboard</h6>
                        <p className="mb-0 small opacity-75" style={{ fontSize: '0.7rem' }}>Real-time insights and performance metrics</p>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <button 
                        className="btn btn-sm"
                        onClick={() => handleShowChart('project-trend')}
                        style={{ 
                          borderRadius: '12px', 
                          fontSize: '0.85rem',
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          border: 'none',
                          color: '#1e40af',
                          fontWeight: '600'
                        }}
                      >
                        <i className="ti ti-chart-dots me-1"></i>
                        View Charts
                      </button>
                    </div>
                  </div>
                </div>

                <div className="card-body p-3">
                  {/* Main KPI Cards Grid */}
                  <div className="row g-4 mb-4">
                    {/* Success Rate Card */}
                    <div className="col-lg-3 col-md-6">
                      <div className="position-relative overflow-hidden h-100 d-flex flex-column" style={{ 
                        background: 'linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)',
                        borderRadius: '16px',
                        padding: '1.5rem',
                        color: 'white',
                        boxShadow: '0 8px 25px rgba(30, 58, 138, 0.3)',
                        minHeight: '200px'
                      }}>
                        <div className="position-absolute" style={{ 
                          top: '-20px', 
                          right: '-20px', 
                          width: '80px', 
                          height: '80px',
                          background: 'rgba(255,255,255,0.1)',
                          borderRadius: '50%'
                        }}></div>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <div className="rounded-circle p-2" style={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            width: '50px',
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <i className="ti ti-trophy" style={{ fontSize: '1.5rem' }}></i>
                          </div>
                          <div className="text-end">
                            <div className="d-flex align-items-center">
                              <i className="ti ti-trending-up me-1" style={{ fontSize: '0.8rem' }}></i>
                              <span className="small fw-bold">+5.2%</span>
                            </div>
                          </div>
                        </div>
                        <h2 className="mb-1 fw-bold" style={{ fontSize: '2rem' }}>92%</h2>
                        <p className="mb-auto opacity-90 fw-medium">Success Rate</p>
                        <button 
                          className="btn btn-sm mt-3 w-100"
                          onClick={() => handleShowChart('success-rate')}
                          style={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            color: 'black',
                            borderRadius: '10px',
                            fontSize: '0.8rem',
                            fontWeight: '600'
                          }}
                        >
                          <i className="ti ti-eye me-1"></i>View Details
                        </button>
                      </div>
                    </div>

                    {/* Active Projects Card */}
                    <div className="col-lg-3 col-md-6">
                      <div className="position-relative overflow-hidden h-100 d-flex flex-column" style={{ 
                        background: 'linear-gradient(135deg, #172554 0%, #1E3A8A 100%)',
                        borderRadius: '16px',
                        padding: '1.5rem',
                        color: 'white',
                        boxShadow: '0 8px 25px rgba(23, 37, 84, 0.3)',
                        minHeight: '200px'
                      }}>
                        <div className="position-absolute" style={{ 
                          top: '-20px', 
                          right: '-20px', 
                          width: '80px', 
                          height: '80px',
                          background: 'rgba(255,255,255,0.1)',
                          borderRadius: '50%'
                        }}></div>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <div className="rounded-circle p-2" style={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            width: '50px',
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <i className="ti ti-rocket" style={{ fontSize: '1.5rem' }}></i>
                          </div>
                          <div className="text-end">
                            <div className="d-flex align-items-center">
                              <i className="ti ti-trending-up me-1" style={{ fontSize: '0.8rem' }}></i>
                              <span className="small fw-bold">+12.5%</span>
                            </div>
                          </div>
                        </div>
                        <h2 className="mb-1 fw-bold" style={{ fontSize: '2rem' }}>
                          {projects.filter(p => p.numberOfRuns > 0).length}
                        </h2>
                        <p className="mb-auto opacity-90 fw-medium">Active Projects</p>
                        <button 
                          className="btn btn-sm mt-3 w-100"
                          onClick={() => handleShowChart('project-trend')}
                          style={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            color: 'black',
                            borderRadius: '10px',
                            fontSize: '0.8rem',
                            fontWeight: '600'
                          }}
                        >
                          <i className="ti ti-eye me-1"></i>View Details
                        </button>
                      </div>
                    </div>

                    {/* Resource Usage Card */}
                    <div className="col-lg-3 col-md-6">
                      <div className="position-relative overflow-hidden h-100 d-flex flex-column" style={{ 
                        background: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)',
                        borderRadius: '16px',
                        padding: '1.5rem',
                        color: 'white',
                        boxShadow: '0 8px 25px rgba(59, 130, 246, 0.3)',
                        minHeight: '200px'
                      }}>
                        <div className="position-absolute" style={{ 
                          top: '-20px', 
                          right: '-20px', 
                          width: '80px', 
                          height: '80px',
                          background: 'rgba(255,255,255,0.1)',
                          borderRadius: '50%'
                        }}></div>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <div className="rounded-circle p-2" style={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            width: '50px',
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <i className="ti ti-cpu" style={{ fontSize: '1.5rem' }}></i>
                          </div>
                          <div className="text-end">
                            <div className="d-flex align-items-center">
                              <i className="ti ti-trending-down me-1" style={{ fontSize: '0.8rem' }}></i>
                              <span className="small fw-bold">-2.1%</span>
                            </div>
                          </div>
                        </div>
                        <h2 className="mb-1 fw-bold" style={{ fontSize: '2rem' }}>78%</h2>
                        <p className="mb-auto opacity-90 fw-medium">Resource Usage</p>
                        <button 
                          className="btn btn-sm mt-3 w-100"
                          onClick={() => handleShowChart('resource-usage')}
                          style={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            color: 'black',
                            borderRadius: '10px',
                            fontSize: '0.8rem',
                            fontWeight: '600'
                          }}
                        >
                          <i className="ti ti-eye me-1"></i>View Details
                        </button>
                      </div>
                    </div>

                    {/* Completion Time Card */}
                    <div className="col-lg-3 col-md-6">
                      <div className="position-relative overflow-hidden h-100 d-flex flex-column" style={{ 
                        background: 'linear-gradient(135deg, #60A5FA 0%, #93C5FD 100%)',
                        borderRadius: '16px',
                        padding: '1.5rem',
                        color: 'white',
                        boxShadow: '0 8px 25px rgba(96, 165, 250, 0.3)',
                        minHeight: '200px'
                      }}>
                        <div className="position-absolute" style={{ 
                          top: '-20px', 
                          right: '-20px', 
                          width: '80px', 
                          height: '80px',
                          background: 'rgba(255,255,255,0.1)',
                          borderRadius: '50%'
                        }}></div>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <div className="rounded-circle p-2" style={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            width: '50px',
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <i className="ti ti-clock" style={{ fontSize: '1.5rem' }}></i>
                          </div>
                          <div className="text-end">
                            <div className="d-flex align-items-center">
                              <i className="ti ti-trending-up me-1" style={{ fontSize: '0.8rem' }}></i>
                              <span className="small fw-bold">+8.3%</span>
                            </div>
                          </div>
                        </div>
                        <h2 className="mb-1 fw-bold" style={{ fontSize: '2rem' }}>24h</h2>
                        <p className="mb-auto opacity-90 fw-medium">Avg Completion</p>
                        <button 
                          className="btn btn-sm mt-3 w-100"
                          onClick={() => handleShowChart('completion-time')}
                          style={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            color: 'black',
                            borderRadius: '10px',
                            fontSize: '0.8rem',
                            fontWeight: '600'
                          }}
                        >
                          <i className="ti ti-eye me-1"></i>View Details
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Progress Section - Extended Full Width */}
                  <div className="row g-4">
                    {/* Project Progress Chart - Full Width */}
                    <div className="col-12">
                      <div className="card border-0 shadow-lg" style={{ 
                        borderRadius: '20px',
                        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                        border: '1px solid rgba(0,0,0,0.05)'
                      }}>
                        <div className="card-header border-0 p-3" style={{ 
                          borderRadius: '20px 20px 0 0',
                          background: 'linear-gradient(135deg, #172554 0%, #1E3A8A 50%, #3B82F6 100%)',
                          color: 'white'
                        }}>
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle p-1 me-2" style={{ 
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                backdropFilter: 'blur(10px)',
                                width: '28px',
                                height: '28px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <i className="ti ti-chart-pie" style={{ fontSize: '1rem', color: 'white' }}></i>
                              </div>
                              <div>
                                <h6 className="mb-0 fw-bold" style={{ color: 'white', fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                                  Project Progress Overview
                                </h6>
                                <p className="mb-0 small opacity-75" style={{ fontSize: '0.7rem' }}>Track project completion status</p>
                              </div>
                            </div>
                            <div className="d-flex gap-2">
                              <button 
                                className="btn btn-sm"
                                style={{ 
                                  borderRadius: '12px', 
                                  fontSize: '0.85rem',
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  border: 'none',
                                  color: '#1e40af',
                                  fontWeight: '600',
                                  padding: '6px 12px'
                                }}
                              >
                                <i className="ti ti-folder me-1"></i>
                                {projects.length} Total
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="card-body p-4">
                          <div className="row g-3">
                            {/* Progress Bars - Full Width Enhanced */}
                            <div className="col-md-12">
                              <div>
                                <div className="mb-5">
                                  <div className="d-flex align-items-center justify-content-between mb-4">
                                    <div className="d-flex align-items-center">
                                      <div className="rounded-circle me-3" style={{ 
                                        width: '18px', 
                                        height: '18px', 
                                        backgroundColor: '#10b981',
                                        boxShadow: '0 0 0 4px rgba(16, 185, 129, 0.2)'
                                      }}></div>
                                      <span className="fw-bold" style={{ fontSize: '1.1rem', color: '#374151' }}>Completed</span>
                                    </div>
                                    <span className="fw-bold" style={{ 
                                      fontSize: '1rem',
                                      color: '#10b981',
                                      backgroundColor: 'rgba(16, 185, 129, 0.15)',
                                      padding: '6px 12px',
                                      borderRadius: '8px'
                                    }}>
                                      {projects.filter(p => p.numberOfRuns > 5).length}/{projects.length}
                                    </span>
                                  </div>
                                  <div className="progress" style={{ 
                                    height: '16px', 
                                    borderRadius: '12px', 
                                    backgroundColor: '#f1f5f9',
                                    overflow: 'hidden',
                                    width: '100%',
                                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                                  }}>
                                    <div 
                                      className="progress-bar" 
                                      role="progressbar" 
                                      style={{ 
                                        width: `${projects.length > 0 ? (projects.filter(p => p.numberOfRuns > 5).length / projects.length) * 100 : 0}%`,
                                        background: 'linear-gradient(90deg, #10b981, #059669)',
                                        borderRadius: '12px',
                                        transition: 'width 0.6s ease-in-out',
                                        boxShadow: '0 4px 8px rgba(16, 185, 129, 0.4)'
                                      }}
                                    ></div>
                                  </div>
                                </div>

                                <div className="mb-5">
                                  <div className="d-flex align-items-center justify-content-between mb-4">
                                    <div className="d-flex align-items-center">
                                      <div className="rounded-circle me-3" style={{ 
                                        width: '18px', 
                                        height: '18px', 
                                        backgroundColor: '#3b82f6',
                                        boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.2)'
                                      }}></div>
                                      <span className="fw-bold" style={{ fontSize: '1.1rem', color: '#374151' }}>In Progress</span>
                                    </div>
                                    <span className="fw-bold" style={{ 
                                      fontSize: '1rem',
                                      color: '#3b82f6',
                                      backgroundColor: 'rgba(59, 130, 246, 0.15)',
                                      padding: '6px 12px',
                                      borderRadius: '8px'
                                    }}>
                                      {projects.filter(p => p.numberOfRuns > 0 && p.numberOfRuns <= 5).length}/{projects.length}
                                    </span>
                                  </div>
                                  <div className="progress" style={{ 
                                    height: '16px', 
                                    borderRadius: '12px', 
                                    backgroundColor: '#f1f5f9',
                                    overflow: 'hidden',
                                    width: '100%',
                                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                                  }}>
                                    <div 
                                      className="progress-bar" 
                                      role="progressbar" 
                                      style={{ 
                                        width: `${projects.length > 0 ? (projects.filter(p => p.numberOfRuns > 0 && p.numberOfRuns <= 5).length / projects.length) * 100 : 0}%`,
                                        background: 'linear-gradient(90deg, #3b82f6, #1e40af)',
                                        borderRadius: '12px',
                                        transition: 'width 0.6s ease-in-out',
                                        boxShadow: '0 4px 8px rgba(59, 130, 246, 0.4)'
                                      }}
                                    ></div>
                                  </div>
                                </div>

                                <div className="mb-3">
                                  <div className="d-flex align-items-center justify-content-between mb-4">
                                    <div className="d-flex align-items-center">
                                      <div className="rounded-circle me-3" style={{ 
                                        width: '18px', 
                                        height: '18px', 
                                        backgroundColor: '#f59e0b',
                                        boxShadow: '0 0 0 4px rgba(245, 158, 11, 0.2)'
                                      }}></div>
                                      <span className="fw-bold" style={{ fontSize: '1.1rem', color: '#374151' }}>Planning</span>
                                    </div>
                                    <span className="fw-bold" style={{ 
                                      fontSize: '1rem',
                                      color: '#f59e0b',
                                      backgroundColor: 'rgba(245, 158, 11, 0.15)',
                                      padding: '6px 12px',
                                      borderRadius: '8px'
                                    }}>
                                      {projects.filter(p => p.numberOfRuns === 0).length}/{projects.length}
                                    </span>
                                  </div>
                                  <div className="progress" style={{ 
                                    height: '16px', 
                                    borderRadius: '12px', 
                                    backgroundColor: '#f1f5f9',
                                    overflow: 'hidden',
                                    width: '100%',
                                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                                  }}>
                                    <div 
                                      className="progress-bar" 
                                      role="progressbar" 
                                      style={{ 
                                        width: `${projects.length > 0 ? (projects.filter(p => p.numberOfRuns === 0).length / projects.length) * 100 : 0}%`,
                                        background: 'linear-gradient(90deg, #f59e0b, #d97706)',
                                        borderRadius: '12px',
                                        transition: 'width 0.6s ease-in-out',
                                        boxShadow: '0 4px 8px rgba(245, 158, 11, 0.4)'
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Project Statistics Section - Full Width */}
                    <div className="col-12 mb-4">
                      <div className="card border-0 shadow-lg" style={{ 
                        borderRadius: '20px',
                        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                        border: '1px solid rgba(0,0,0,0.05)'
                      }}>
                        <div className="card-header border-0 p-2" style={{ 
                          borderRadius: '20px 20px 0 0',
                          background: 'linear-gradient(135deg, #172554 0%, #1E3A8A 50%, #3B82F6 100%)',
                          color: 'white'
                        }}>
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle p-1 me-2" style={{ 
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                backdropFilter: 'blur(10px)',
                                width: '28px',
                                height: '28px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <i className="ti ti-chart-dots" style={{ fontSize: '1rem', color: 'white' }}></i>
                              </div>
                              <div>
                                <h6 className="mb-0 fw-bold" style={{ color: 'white', fontSize: '0.95rem' }}>Project Statistics</h6>
                                <p className="mb-0 small opacity-75" style={{ fontSize: '0.7rem' }}>Key performance metrics overview</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="card-body p-4">
                          <div className="d-flex justify-content-center">
                            <div className="w-100 p-4" style={{ 
                              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
                              borderRadius: '20px',
                              border: '1px solid rgba(226, 232, 240, 0.8)',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                              backdropFilter: 'blur(10px)',
                              maxWidth: '600px'
                            }}>
                              <div className="row g-4">
                                <div className="col-6">
                                  <div className="text-center">
                                    <div className="mb-2" style={{ 
                                      fontSize: '1.5rem', 
                                      fontWeight: '800',
                                      background: 'linear-gradient(135deg, #10b981, #059669)',
                                      WebkitBackgroundClip: 'text',
                                      WebkitTextFillColor: 'transparent',
                                      backgroundClip: 'text',
                                      filter: 'drop-shadow(0 2px 4px rgba(16, 185, 129, 0.2))'
                                    }}>
                                      {Math.round((projects.filter(p => p.numberOfRuns > 5).length / Math.max(projects.length, 1)) * 100)}%
                                    </div>
                                    <p className="mb-0 text-muted fw-medium" style={{ 
                                      fontSize: '0.45rem !important',
                                      letterSpacing: '0.5px'
                                    }}>Completion</p>
                                  </div>
                                </div>
                                <div className="col-6">
                                  <div className="text-center">
                                    <div className="mb-2" style={{ 
                                      fontSize: '1.5rem', 
                                      fontWeight: '800',
                                      background: 'linear-gradient(135deg, #3b82f6, #1e40af)',
                                      WebkitBackgroundClip: 'text',
                                      WebkitTextFillColor: 'transparent',
                                      backgroundClip: 'text',
                                      filter: 'drop-shadow(0 2px 4px rgba(59, 130, 246, 0.2))'
                                    }}>
                                      {projects.reduce((total, project) => total + (project.numberOfRuns || 0), 0)}
                                    </div>
                                    <p className="mb-0 text-muted fw-medium" style={{ 
                                      fontSize: '0.5rem !important',
                                      letterSpacing: '0.5px'
                                    }}>Total Runs</p>
                                  </div>
                                </div>
                                <div className="col-6">
                                  <div className="text-center">
                                    <div className="mb-2" style={{ 
                                      fontSize: '1.5rem', 
                                      fontWeight: '800',
                                      background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
                                      WebkitBackgroundClip: 'text',
                                      WebkitTextFillColor: 'transparent',
                                      backgroundClip: 'text',
                                      filter: 'drop-shadow(0 2px 4px rgba(6, 182, 212, 0.2))'
                                    }}>
                                      {new Set(projects.map(p => p.projectOwner)).size}
                                    </div>
                                    <p className="mb-0 text-muted fw-medium" style={{ 
                                      fontSize: '0.5rem !important',
                                      letterSpacing: '0.5px'
                                    }}>Team</p>
                                  </div>
                                </div>
                                <div className="col-6">
                                  <div className="text-center">
                                    <div className="mb-2" style={{ 
                                      fontSize: '1.5rem', 
                                      fontWeight: '800',
                                      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                      WebkitBackgroundClip: 'text',
                                      WebkitTextFillColor: 'transparent',
                                      backgroundClip: 'text',
                                      filter: 'drop-shadow(0 2px 4px rgba(245, 158, 11, 0.2))'
                                    }}>
                                      {projects.filter(p => p.numberOfRuns > 0).length}
                                    </div>
                                    <p className="mb-0 text-muted fw-medium" style={{ 
                                      fontSize: '0.5rem !important',
                                      letterSpacing: '0.5px'
                                    }}>Active</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Activity Row */}
                  <div className="row g-4">
                    {/* Recent Activity */}
                    <div className="col-12">
                      <div className="card border-0 shadow-lg" style={{ 
                        borderRadius: '20px',
                        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                        border: '1px solid rgba(0,0,0,0.05)'
                      }}>
                        <div className="card-header border-0 p-2" style={{ 
                          borderRadius: '16px 16px 0 0',
                          background: '#3B82F6',
                          color: 'white'
                        }}>
                          <div className="d-flex align-items-center">
                            <div className="rounded-circle p-1 me-2" style={{ 
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              backdropFilter: 'blur(10px)',
                              width: '28px',
                              height: '28px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <i className="ti ti-activity" style={{ fontSize: '1rem', color: 'white' }}></i>
                            </div>
                            <div>
                              <h6 className="mb-0 fw-bold" style={{ color: 'white', fontSize: '0.95rem' }}>Recent Activity</h6>
                              <p className="mb-0 small opacity-75" style={{ fontSize: '0.7rem' }}>Latest Updates</p>
                            </div>
                          </div>
                        </div>
                        <div className="card-body p-3">
                          <div className="activity-timeline">
                            <div className="d-flex align-items-center mb-3">
                              <div className="rounded-circle p-2 me-3" style={{ 
                                backgroundColor: '#dcfce7', 
                                width: '40px', 
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <i className="ti ti-plus" style={{ fontSize: '1rem', color: '#16a34a' }}></i>
                              </div>
                              <div className="flex-grow-1">
                                <p className="mb-1 fw-medium" style={{ fontSize: '0.5rem !important', lineHeight: '1.2 !important' }}>New project created</p>
                                <p className="text-muted mb-0" style={{ fontSize: '0.45rem !important', lineHeight: '1.1 !important' }}>2 hours ago</p>
                              </div>
                              <div className="text-end">
                                <span className="badge bg-success" style={{ borderRadius: '8px', fontSize: '0.4rem !important', padding: '2px 4px !important' }}>New</span>
                              </div>
                            </div>

                            <div className="d-flex align-items-center mb-3">
                              <div className="rounded-circle p-2 me-3" style={{ 
                                backgroundColor: '#dbeafe', 
                                width: '40px', 
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <i className="ti ti-check" style={{ fontSize: '1rem', color: '#3b82f6' }}></i>
                              </div>
                              <div className="flex-grow-1">
                                <p className="mb-1 fw-medium" style={{ fontSize: '0.5rem !important', lineHeight: '1.2 !important' }}>Analysis completed</p>
                                <p className="text-muted mb-0" style={{ fontSize: '0.45rem !important', lineHeight: '1.1 !important' }}>4 hours ago</p>
                              </div>
                              <div className="text-end">
                                <span className="badge bg-primary" style={{ borderRadius: '8px', fontSize: '0.4rem !important', padding: '2px 4px !important' }}>Done</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Quick Actions Sidebar */}
            <div className="col-md-4">
              {/* Enhanced Quick Links */}
              <div className="card border-0 shadow-lg" style={{ 
                borderRadius: '20px',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(0,0,0,0.05)'
              }}>
                <div className="card-header border-0 p-2" style={{ 
                  borderRadius: '20px 20px 0 0',
                  background: '#4B7BE7',
                  color: 'white'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle p-1 me-2" style={{ 
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(10px)',
                      width: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <i className="ti ti-external-link" style={{ fontSize: '1rem', color: 'white' }}></i>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-bold" style={{ color: 'white', fontSize: '0.95rem' }}>Quick Links</h6>
                      <p className="mb-0 small opacity-75" style={{ fontSize: '0.7rem' }}>Easy access to important resources</p>
                    </div>
                  </div>
                </div>
                <div className="card-body p-3">
                  <div className="row g-3">
                    {/* Getting Started */}
                    <div className="col-12">
                      <button 
                        className="w-100 btn text-start p-2 border-0"
                        style={{ 
                          backgroundColor: '#f0f9ff', 
                          borderRadius: '12px',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => setShowProjectModal(true)}
                      >
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle p-2 me-3" style={{ 
                            backgroundColor: '#1e40af', 
                            width: '40px', 
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <i className="ti ti-rocket" style={{ fontSize: '1.2rem', color: 'white' }}></i>
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-bold" style={{ color: '#1e293b' }}>Getting Started</h6>
                            <p className="text-muted mb-0 small">Create your first project</p>
                          </div>
                        </div>
                      </button>
                    </div>

                    {/* Documentation */}
                    <div className="col-12">
                      <a 
                        href="https://code.visualstudio.com/docs" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="w-100 btn text-start p-2 border-0 text-decoration-none"
                        style={{ 
                          backgroundColor: '#f0fdf4', 
                          borderRadius: '12px',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle p-2 me-3" style={{ 
                            backgroundColor: '#059669', 
                            width: '40px', 
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <i className="ti ti-book" style={{ fontSize: '1.2rem', color: 'white' }}></i>
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-bold" style={{ color: '#1e293b' }}>Documentation</h6>
                            <p className="text-muted mb-0 small">Learn about features</p>
                          </div>
                        </div>
                      </a>
                    </div>

                    {/* Support */}
                    <div className="col-12">
                      <a 
                        href="https://stackoverflow.com/" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="w-100 btn text-start p-2 border-0 text-decoration-none"
                        style={{ 
                          backgroundColor: '#fefce8', 
                          borderRadius: '12px',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle p-2 me-3" style={{ 
                            backgroundColor: '#d97706', 
                            width: '40px', 
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <i className="ti ti-help" style={{ fontSize: '1.2rem', color: 'white' }}></i>
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-bold" style={{ color: '#1e293b' }}>Support</h6>
                            <p className="text-muted mb-0 small">Get help from community</p>
                          </div>
                        </div>
                      </a>
                    </div>

                    {/* Updates */}
                    <div className="col-12">
                      <a 
                        href="https://github.com/" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="w-100 btn text-start p-2 border-0 text-decoration-none"
                        style={{ 
                          backgroundColor: '#fdf2f8', 
                          borderRadius: '12px',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle p-2 me-3" style={{ 
                            backgroundColor: '#ec4899', 
                            width: '40px', 
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <i className="ti ti-bell" style={{ fontSize: '1.2rem', color: 'white' }}></i>
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-bold" style={{ color: '#1e293b' }}>Updates</h6>
                            <p className="text-muted mb-0 small">Latest news & features</p>
                          </div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ProjectModal 
        show={showProjectModal}
        onHide={() => setShowProjectModal(false)}
        onProjectCreated={handleProjectCreated}
      />

      {/* Chart Modal */}
      {showChartModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center">
                  <i className="ti ti-chart-line me-2 text-primary"></i>
                  Analytics Chart
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseChart}></button>
              </div>
              <div className="modal-body p-4">
                {renderChart()}
                
                {/* Chart Type Selector */}
                <div className="mt-4 pt-3 border-top">
                  <h6 className="fw-bold mb-3">Select Chart Type:</h6>
                  <div className="btn-group w-100" role="group">
                    <button 
                      type="button" 
                      className={`btn ${selectedChart === 'project-trend' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setSelectedChart('project-trend')}
                    >
                      <i className="ti ti-chart-line me-1"></i>
                      Project Trends
                    </button>
                    <button 
                      type="button" 
                      className={`btn ${selectedChart === 'success-rate' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setSelectedChart('success-rate')}
                    >
                      <i className="ti ti-chart-bar me-1"></i>
                      Success Rate
                    </button>
                    <button 
                      type="button" 
                      className={`btn ${selectedChart === 'resource-usage' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setSelectedChart('resource-usage')}
                    >
                      <i className="ti ti-chart-area me-1"></i>
                      Resource Usage
                    </button>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseChart}>
                  Close
                </button>
                <button type="button" className="btn btn-primary">
                  <i className="ti ti-download me-1"></i>
                  Export Chart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Project</h5>
                <button type="button" className="btn-close" onClick={handleDeleteCancel}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete the project:</p>
                <strong>"{projectToDelete?.projectName}"</strong>
                <p className="mt-2 text-danger">
                  <small>This action cannot be undone. All project data will be permanently deleted.</small>
                </p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleDeleteCancel} 
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={handleDeleteConfirm} 
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Project'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Project Creation Confirmation Modal */}
      {showConfirmationModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="ti-check text-success me-2"></i>
                  Project Created Successfully!
                </h5>
                <button type="button" className="btn-close" onClick={handleStayOnMainPage}></button>
              </div>
              <div className="modal-body">
                <p>Your project <strong>"{createdProject?.projectName}"</strong> has been created successfully.</p>
                <p>Would you like to go to the Project Landing page to start managing your project?</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleStayOnMainPage}
                >
                  Stay Here
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={handleGoToProjectLanding}
                >
                  Go to Project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MainPage;
